import { useState } from 'react';
import type { RoomView, RoundHistoryEntry } from '@bm/shared';
import { de } from '@/i18n/de';
import { Sheet } from '@/components/Sheet';

function nameOf(view: RoomView, playerId: string): string {
  return view.players.find((player) => player.id === playerId)?.name ?? playerId;
}

function summaryOf(view: RoomView, entry: RoundHistoryEntry): string {
  switch (entry.result.type) {
    case 'ELIMINATION':
      return `${nameOf(view, entry.result.playerId)} · ${de.roles[entry.result.role]}`;
    case 'NO_ELIMINATION':
      return de.history.noElimination;
    case 'TIE':
      return de.roundResult.tie;
  }
}

function HistoryEntryCard({ entry, view }: { entry: RoundHistoryEntry; view: RoomView }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-surface-border bg-bg">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div>
          <div className="font-display font-semibold">
            {de.history.round(entry.roundNumber)}
            {entry.isTieBreaker && (
              <span className="ml-2 rounded-full bg-surface-2 px-2 py-0.5 text-xs text-fg-muted">
                {de.history.tieBreaker}
              </span>
            )}
          </div>
          <div className="text-sm text-fg-muted">{summaryOf(view, entry)}</div>
        </div>
        <span className="text-fg-faint">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="border-t border-surface-border px-4 py-3 text-sm">
          <div className="mb-1 font-medium text-fg-muted">{de.history.votes}</div>
          <ul className="flex flex-col gap-1">
            {entry.votes.map((vote) => (
              <li key={vote.voterId} className="flex justify-between text-fg-muted">
                <span>{nameOf(view, vote.voterId)}</span>
                <span>
                  →{' '}
                  {vote.targetId === null ? de.voting.abstain : nameOf(view, vote.targetId)}
                </span>
              </li>
            ))}
          </ul>

          {/* Sonderrollen können Stimmen verstummen lassen oder verdoppeln — ohne
              die Auszählung daneben wäre die Differenz nicht nachvollziehbar. */}
          {entry.effects.length > 0 && (
            <>
              <div className="mt-3 mb-1 font-medium text-fg-muted">{de.history.tally}</div>
              <ul className="flex flex-col gap-1">
                {entry.tally
                  .filter((row) => row.count > 0)
                  .map((row) => (
                    <li key={row.playerId} className="flex justify-between text-fg-muted">
                      <span>{nameOf(view, row.playerId)}</span>
                      <span>{de.roundResult.votesLabel(row.count)}</span>
                    </li>
                  ))}
              </ul>

              <div className="mt-3 mb-1 font-medium text-fg-muted">{de.history.effects}</div>
              <ul className="flex flex-col gap-1 text-fg-muted">
                {entry.effects.map((effect) => (
                  <li key={effect}>· {de.ballotEffects[effect]}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function HistorySheet({
  view,
  open,
  onClose,
}: {
  view: RoomView;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={open} onClose={onClose} title={de.history.title}>
      {view.history.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-faint">{de.history.empty}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {[...view.history].reverse().map((entry) => (
            <HistoryEntryCard key={`${entry.roundNumber}-${entry.ballotNumber}`} entry={entry} view={view} />
          ))}
        </div>
      )}
    </Sheet>
  );
}
